import { STANDARD_TIMEFRAMES, type Timeframe } from './chartModel';

// Approximate widths in pixels, used to decide what fits on one toolbar line.
const GAP = 6;
const PADDING = 16;
const SYMBOL = 96;
const TYPE = 76;
const TIMEFRAME_BUTTON = 29;
const TIMEFRAME_SELECT = 66;
/** The drop-down with the other twelve timeframes, next to the buttons. */
const MORE_TIMEFRAMES = 44;
export const ICON_BUTTON = 28;

export interface ToolbarFit {
  /** Too narrow for the row of timeframe buttons: a drop-down instead. */
  timeframeSelect: boolean;
  /** How many of the icon buttons fit before the rest go into the "More" menu. */
  icons: number;
}

/** What fits on one line of a chart toolbar this wide with `iconCount` icon buttons, so it never wraps. */
export function toolbarFit(width: number, iconCount: number): ToolbarFit {
  const fixed = (timeframes: number) => PADDING + SYMBOL + TYPE + timeframes + GAP * 4;
  const room = (timeframes: number) => width - fixed(timeframes);
  const buttons = STANDARD_TIMEFRAMES.length * TIMEFRAME_BUTTON + MORE_TIMEFRAMES;
  const iconsIn = (space: number, withMore: boolean) =>
    Math.max(0, Math.floor((space - (withMore ? ICON_BUTTON + GAP : 0)) / (ICON_BUTTON + 2)));

  // Keep the timeframe buttons while the four most used tools still fit next to them.
  const timeframeSelect = iconsIn(room(buttons), true) < Math.min(4, iconCount);
  const space = room(timeframeSelect ? TIMEFRAME_SELECT : buttons);
  const all = iconsIn(space, false);
  return { timeframeSelect, icons: all >= iconCount ? iconCount : iconsIn(space, true) };
}

/** Keys 1 to 9 pick M1 to MN1, the nine of the toolbar, in order. */
export function timeframeForKey(key: string): Timeframe | null {
  if (!/^[1-9]$/.test(key)) return null;
  return STANDARD_TIMEFRAMES[Number(key) - 1] ?? null;
}

/** The bar spacing after a zoom step, within what stays readable. */
export function zoomedSpacing(current: number, direction: 1 | -1): number {
  const next = direction > 0 ? current * 1.25 : current / 1.25;
  return Math.min(60, Math.max(1, Math.round(next * 100) / 100));
}

/** An undo history: the newest state last, at most `max` kept. */
export function pushHistory<T>(stack: readonly T[], item: T, max = 50): T[] {
  return [...stack, item].slice(-max);
}

/** A file name for a chart picture: "EURUSD_H1_2026-09-29_1405.png". */
export function screenshotName(symbol: string, tf: Timeframe, now: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())}_${two(now.getHours())}${two(now.getMinutes())}`;
  return `${symbol.replace(/[^\w.-]/g, '')}_${tf}_${stamp}.png`;
}
